import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import type { Ask } from '@spi/shared';
import { SeedFormat } from '@spi/shared';
import { useFulfillAsk } from '../../../api/donations';

const schema = z.object({
  quantityKg: z.coerce.number().positive('Quantity must be positive').optional(),
  quantityPackets: z.coerce.number().int().positive('Quantity must be positive').optional(),
  fairMarketValueUsd: z.coerce.number().positive('Value must be positive'),
});

interface Props {
  ask: Ask;
  onClose: () => void;
}

export function FulfillAskModal({ ask, onClose }: Props) {
  const fulfillAsk = useFulfillAsk();
  const remainingKg = ask.quantityKg ? ask.quantityKg - ask.fulfilledKg : undefined;
  const remainingPackets = ask.quantityPackets ? ask.quantityPackets - ask.fulfilledPackets : undefined;

  const showKg = ask.seedFormat === SeedFormat.BULK || ask.seedFormat === SeedFormat.BOTH;
  const showPackets = ask.seedFormat === SeedFormat.PACKET || ask.seedFormat === SeedFormat.BOTH;

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      quantityKg: remainingKg ?? undefined,
      quantityPackets: remainingPackets ?? undefined,
      fairMarketValueUsd: 0,
    },
  });

  const submit = async (data: { quantityKg?: number; quantityPackets?: number; fairMarketValueUsd: number }) => {
    try {
      await fulfillAsk.mutateAsync({
        askId: ask.id,
        quantityKg: showKg ? data.quantityKg : undefined,
        quantityPackets: showPackets ? data.quantityPackets : undefined,
        fairMarketValueUsd: data.fairMarketValueUsd,
      });
      toast.success('Pledge created!');
      onClose();
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to create pledge');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <h3 className="text-xl font-semibold text-spi-charcoal mb-2">Pledge Seeds</h3>
        <p className="text-sm text-spi-slate mb-1">
          <strong>{ask.crop}</strong>{ask.preferredVarieties ? ` (${ask.preferredVarieties})` : ''}
        </p>
        <p className="text-xs text-spi-slate mb-6">
          {showKg && remainingKg != null && `${remainingKg} kg remaining`}
          {showKg && showPackets && ' / '}
          {showPackets && remainingPackets != null && `${remainingPackets} packets remaining`}
        </p>

        <form onSubmit={handleSubmit(submit)} className="space-y-4">
          {showKg && (
            <div>
              <label className="input-label">Quantity (kg)</label>
              <input {...register('quantityKg')} type="number" step="0.1" className="input" />
              {errors.quantityKg && <p className="text-red-500 text-xs mt-1">{errors.quantityKg.message}</p>}
            </div>
          )}
          {showPackets && (
            <div>
              <label className="input-label">Quantity (packets)</label>
              <input {...register('quantityPackets')} type="number" step="1" className="input" />
              {errors.quantityPackets && <p className="text-red-500 text-xs mt-1">{errors.quantityPackets.message}</p>}
            </div>
          )}
          <div>
            <label className="input-label">Fair Market Value (USD)</label>
            <input {...register('fairMarketValueUsd')} type="number" step="0.01" className="input" />
            {errors.fairMarketValueUsd && <p className="text-red-500 text-xs mt-1">{errors.fairMarketValueUsd.message}</p>}
            <p className="text-xs text-spi-slate mt-1">Used for IRS Form 8283 tax deduction</p>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="btn btn-primary flex-1">
              {isSubmitting ? 'Pledging...' : 'Confirm Pledge'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
